// src/ui/ThemeProvider.jsx
// ─── Source unique du thème (§16-20, §32-34 du cahier 2) ───────────────────
//
// Le symptôme : « espace principal en thème clair, certaines modales s'ouvrent
// en sombre ». La cause n'est pas dans les modales fautives — elle est dans
// l'absence de source de vérité. Le thème vivait à TROIS endroits :
//
//   · un IIFE au chargement du module App.jsx, qui pose `body.dark-mode` ;
//   · un `useState(darkMode)` dans le composant racine ;
//   · et, dans chaque modale développée séparément, des couleurs en dur
//     (`background: #111`) qui ne consultaient rien du tout.
//
// Une modale développée pendant une période où le sombre était le défaut est
// donc restée sombre pour toujours. Corriger ces modales une par une aurait
// laissé la cause intacte : la quatrième modale écrite le mois prochain aurait
// reproduit le problème.
//
// Ici, un seul état, exposé de trois façons pour que TOUT puisse le lire :
//   1. `data-theme` sur <html>  → les variables CSS (aucun JS requis) ;
//   2. la classe `dark-mode` sur <body> → compatibilité avec l'existant ;
//   3. le contexte React        → les composants qui ont besoin de la valeur.
//
// §33 — Pas de flicker : le thème est posé sur <html> AVANT le premier rendu
// (voir applyStoredTheme, appelée à l'évaluation du module).

import { createContext, useContext, useState, useEffect, useCallback, useMemo } from "react";

const STORAGE_KEY = "viq_theme_v288";
const ThemeContext = createContext({ theme: "light", dark: false, setTheme: () => {}, toggle: () => {} });

export function readStoredTheme() {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved === "dark" || saved === "light") return saved;
  } catch (e) { /* stockage indisponible : mode privé, quota */ }
  return null;
}

/**
 * Applique le thème au document. Idempotent, appelable avant React.
 *
 * §33 — Appelée à l'évaluation du module (voir plus bas), donc AVANT le
 * premier rendu : aucune transition dark → light de 100 ms à l'ouverture.
 */
export function applyTheme(theme) {
  if (typeof document === "undefined") return;
  const dark = theme === "dark";
  const root = document.documentElement;
  root.setAttribute("data-theme", dark ? "dark" : "light");
  root.style.colorScheme = dark ? "dark" : "light";
  // La classe historique reste posée : des centaines de règles dans
  // App.jsx et landing.css s'y accrochent encore. La supprimer d'un coup
  // aurait décoloré la moitié de l'application.
  if (document.body) {
    document.body.classList.toggle("dark-mode", dark);
    document.body.classList.toggle("light-mode", !dark);
  }
}

// v289 — Thème global administré. Le réglage du dashboard admin
// (`site_global_theme`, lu via GET /api/public/theme) est la source de vérité
// pour TOUT le site : landing, pages publiques, formulaires leads,
// authentification et espace user. La dernière valeur connue est mise en cache
// localement uniquement pour éviter un flash au chargement ; elle est
// réaffirmée à chaque ouverture. Sans réglage connu : clair.
const GLOBAL_KEY = "viq_site_theme_global_v289";
export const GLOBAL_THEME_EVENT = "viq-admin-global-theme";

export function readGlobalTheme() {
  try {
    const v = localStorage.getItem(GLOBAL_KEY);
    if (v === "dark" || v === "light") return v;
  } catch (e) { /* stockage indisponible */ }
  return null;
}

export function storeGlobalTheme(theme) {
  const value = theme === "dark" ? "dark" : "light";
  try {
    localStorage.setItem(GLOBAL_KEY, value);
    // Les anciennes préférences locales ne doivent plus masquer le réglage admin.
    localStorage.setItem(STORAGE_KEY, value);
    localStorage.setItem("viq_public_theme_v288", value);
    localStorage.setItem("viq_pro_lead_theme_v288", value);
  } catch (e) { /* non bloquant */ }
  return value;
}

let globalThemeRequest = null;
/**
 * Lit le thème global administré, le met en cache et le diffuse à toutes les
 * surfaces montées (évènement `viq-admin-global-theme`). Une seule requête
 * réseau par chargement de page, partagée par tous les appelants.
 */
export function fetchGlobalTheme(apiUrl = "", { force = false } = {}) {
  if (typeof fetch === "undefined") return Promise.resolve(readGlobalTheme() || "light");
  if (globalThemeRequest && !force) return globalThemeRequest;
  const url = `${apiUrl || ""}/api/public/theme${force ? `?t=${Date.now()}` : ""}`;
  globalThemeRequest = fetch(url, { cache: "no-store" })
    .then(r => r.json())
    .then(j => {
      if (!j?.success) return readGlobalTheme() || "light";
      const value = storeGlobalTheme(j.theme);
      try { window.dispatchEvent(new CustomEvent(GLOBAL_THEME_EVENT, { detail: { theme: value, source: "server" } })); } catch (e) {}
      return value;
    })
    .catch(() => readGlobalTheme() || "light");
  return globalThemeRequest;
}

// Le thème de départ est celui fixé par l'administrateur (dernière valeur
// connue), sinon clair. Une bascule faite pendant la session reste locale à
// cette session : au prochain chargement, le réglage global reprend la main.
export function resolveInitialTheme({ preferApp = false } = {}) {
  const global = readGlobalTheme();
  if (global) return global;
  return "light";
}

// Exécuté à l'import, avant tout rendu React (§33).
applyTheme(resolveInitialTheme());

export function ThemeProvider({ children, initial = null }) {
  const [theme, setThemeState] = useState(() => initial || resolveInitialTheme());

  const setTheme = useCallback((next) => {
    const value = next === "dark" ? "dark" : "light";
    setThemeState(value);
    applyTheme(value);
    try { localStorage.setItem(STORAGE_KEY, value); } catch (e) { /* non bloquant */ }
    // §20 — Les modales DÉJÀ ouvertes doivent basculer immédiatement. Celles
    // qui ne consomment pas encore le contexte (composants hérités montés hors
    // de l'arbre) écoutent cet évènement.
    try { window.dispatchEvent(new CustomEvent("viq-theme-change", { detail: { theme: value } })); } catch (e) {}
  }, []);

  const toggle = useCallback(() => setTheme(theme === "dark" ? "light" : "dark"), [theme, setTheme]);

  // §20, §34 — Le basculement est instantané et sans rechargement. L'ancien
  // évènement global reste écouté : le bouton de thème vit dans des composants
  // qui ne reçoivent pas de props depuis la racine.
  useEffect(() => {
    const onToggle = () => setTheme(theme === "dark" ? "light" : "dark");
    const onStorage = (e) => { if (e.key === STORAGE_KEY && e.newValue) setThemeState(e.newValue === "dark" ? "dark" : "light"); };
    window.addEventListener("viq-toggle-theme", onToggle);
    window.addEventListener("storage", onStorage);
    return () => {
      window.removeEventListener("viq-toggle-theme", onToggle);
      window.removeEventListener("storage", onStorage);
    };
  }, [theme, setTheme]);

  // Le DOM peut avoir été touché ailleurs (code hérité, retour arrière du
  // navigateur) : on réaffirme l'état à chaque changement.
  useEffect(() => { applyTheme(theme); }, [theme]);

  const value = useMemo(() => ({ theme, dark: theme === "dark", setTheme, toggle }), [theme, setTheme, toggle]);
  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme() { return useContext(ThemeContext); }

// Bouton de bascule normalisé, utilisable partout — y compris dans une modale.
export function ThemeToggle({ className = "", label = true }) {
  const { dark, toggle } = useTheme();
  return (
    <button type="button" className={`ws-btn ghost icon ${className}`} onClick={toggle}
      aria-label={dark ? "Passer en thème clair" : "Passer en thème sombre"}
      title={dark ? "Thème clair" : "Thème sombre"}>
      <span aria-hidden="true">{dark ? "☀" : "☾"}</span>
      {label && <span className="ws-btn-label">{dark ? "Clair" : "Sombre"}</span>}
    </button>
  );
}

export default ThemeProvider;
